import type { DisciplineId } from '@obraplan/core/domain/shared/ids';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import type { UnitOfMeasure } from '@obraplan/core/domain/schedule/unit-of-measure';
import { measureShortLabel, measureUnit } from '@obraplan/core/domain/takeoff/applies-to';
import { FLOOR_PLAN_KIND_LABEL } from '@obraplan/core/domain/takeoff/floor-plan';
import {
  listarDisciplinas,
  type DestinoDeEstacas,
  type DisciplinaListada,
  type ServicoDesativado,
  type ServicoDisponivel,
  type ServicoListado,
} from '@obraplan/core/domain/takeoff/planilha-de-indices';
import type { Service } from '@obraplan/core/domain/takeoff/service';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { STAGE_REGISTRY, type Stage } from '@obraplan/core/domain/takeoff/stage-registry';
import {
  isSteelPurchaseService,
  STEEL_GAUGES,
  steelGaugeLabel,
} from '@obraplan/core/domain/takeoff/steel';
import { ITEM_KIND_LABEL } from '@obraplan/core/domain/takeoff/takeoff-line';
import type { XlsxCell, XlsxSheet } from '@/features/reports/xlsx';
import type { WorkspaceDraft } from './typology-draft';

/**
 * O modelo de índices: o catálogo da obra como planilha para preencher (Fase 27, D17, F11).
 *
 * O usuário não parte de uma folha em branco. Ele recebe, da própria obra, uma linha por serviço
 * × item — com o índice e a perda que já existem —, preenche o que falta e importa de volta
 * pelo mesmo botão (ADR-177). Quatro abas, que `lerPlanilhaDeIndices` reconhece pelo cabeçalho:
 *
 * - **Índices**: Etapa | Disciplina | Serviço | Unidade do serviço | Mede por | Item | Código |
 *   Tipo | Unidade do item | Índice | Perda %;
 * - **Taxas de aço**: Disciplina | Bitola | kg/m³, uma linha por bitola de cada disciplina que
 *   tem CORTE E DOBRA DE FERRAGEM — o único serviço que compra aço (RN-366);
 * - **Tipos**: Tipo | Diâmetro | Comprimento | Bitola | kg por estaca, uma linha por tipo ×
 *   bitola, com o diâmetro e o comprimento repetidos em **metro** (D1, D17). O tipo sem aço sai
 *   numa linha só, com a bitola e os kg vazios;
 * - **Estacas**: Nome | Tipo | Pavimento, uma linha por estaca de cada desenho. O Pavimento é o
 *   **nome da tipologia**, que não se repete na obra: o nome do pavimento da estrutura pode
 *   valer para duas tipologias, e o modelo que o escrevesse não voltaria igual (F10).
 *
 * Sem tipo e sem estaca na obra, as duas abas saem só com o cabeçalho — e continuam sendo o
 * convite a escrever o primeiro.
 *
 * As colunas Etapa, Unidade do serviço e Mede por são **informação** para quem preenche, e o
 * import as ignora: sem elas o índice "0,4" não diz se é por m², por m³ ou por kg.
 *
 * Numa obra nova os serviços ainda não têm item. O modelo traz, então, uma linha com o Item
 * vazio por serviço sem item — o convite a escrever o primeiro. O nome que o usuário escrever
 * casa com o item da obra pelo código de compras, quando a linha tem um, e senão pela descrição.
 */

const CABECALHO_INDICES = [
  'Etapa',
  'Disciplina',
  'Serviço',
  'Unidade do serviço',
  'Mede por',
  'Item',
  'Código',
  'Tipo',
  'Unidade do item',
  'Índice',
  'Perda %',
] as const;

const CABECALHO_TAXAS = ['Disciplina', 'Bitola', 'kg/m³'] as const;

const CABECALHO_TIPOS = ['Tipo', 'Diâmetro', 'Comprimento', 'Bitola', 'kg por estaca'] as const;

const CABECALHO_ESTACAS = ['Nome', 'Tipo', 'Pavimento'] as const;

const ROTULO_DA_ETAPA: Readonly<Record<Stage, string>> = {
  PRELIMINAR: 'Preliminar',
  FUNDACAO: 'Fundação',
  CONTENCAO: 'Contenção',
  ESTRUTURA: 'Estrutura',
};

/** A etapa de cada disciplina do registro, e a posição dela — que é a ordem de execução. */
const DO_REGISTRO: ReadonlyMap<string, { readonly etapa: string; readonly posicao: number }> =
  new Map(
    STAGE_REGISTRY.map((disciplina, posicao) => [
      normalizeName(disciplina.name),
      { etapa: ROTULO_DA_ETAPA[disciplina.stage], posicao },
    ]),
  );

/**
 * As disciplinas na ordem em que a obra acontece, e as que o registro não conhece depois.
 *
 * É a ordem do `STAGE_REGISTRY` — de topografia a escada —, e não a do catálogo: quem
 * preenche percorre a planilha como percorre a obra. A ordenação é estável, então as
 * disciplinas fora do registro mantêm a ordem em que o catálogo as tinha.
 */
function naOrdemDaObra(disciplinas: readonly DisciplinaListada[]): readonly DisciplinaListada[] {
  const posicao = (disciplina: DisciplinaListada): number =>
    DO_REGISTRO.get(normalizeName(disciplina.name))?.posicao ?? Number.MAX_SAFE_INTEGER;
  return [...disciplinas].sort((a, b) => posicao(a) - posicao(b));
}

/**
 * "volume de pilar (m³)" — a base em que o índice foi pensado, escrita por extenso.
 *
 * Reaproveita o texto curto da tela do serviço, que já separa "faces de pilar" de "faces de
 * viga". Sem a peça escolhida ele diz "sem elemento", e isso é informação: o serviço
 * pendente não mede, e o índice dele ainda não calcula nada.
 */
function medePor(servico: ServicoListado): string {
  const curta = measureShortLabel({
    measureBy: servico.measureBy,
    ...(servico.appliesTo === undefined ? {} : { appliesTo: servico.appliesTo }),
  });
  const unidade = measureUnit(servico.appliesTo, servico.measureBy);
  return unidade === '' ? curta : `${curta} (${unidade})`;
}

function linhasDoServico(
  etapa: string,
  disciplina: DisciplinaListada,
  servico: ServicoListado,
): readonly (readonly XlsxCell[])[] {
  const doServico = [etapa, disciplina.name, servico.name, servico.unit ?? '', medePor(servico)];
  // Vazio, e não "—": a célula vazia é o que o filtro do Excel separa como "(Vazias)".
  if (servico.items.length === 0) return [[...doServico, '', '', '', '', '', '']];
  return servico.items.map((item) => [
    ...doServico,
    item.description,
    item.code ?? '',
    ITEM_KIND_LABEL[item.kind],
    item.unit ?? '',
    // Número, e não texto: o Excel mostra na vírgula de quem abriu, e soma se quiser.
    item.consumptionIndex ?? '',
    item.lossPercent ?? '',
  ]);
}

/**
 * O que a obra tem de fundação, na forma que as duas abas novas precisam: os tipos de estaca e
 * os desenhos com as estacas deles. O `WorkspaceDraft` inteiro serve, e o modelo só olha estas
 * chaves.
 */
export type ObraDeEstacas = Pick<WorkspaceDraft, 'towers' | 'typologies' | 'pileTypes'>;

/**
 * Os desenhos que podem receber estacas: tipologia de **área comum** de Fundação ou Contenção
 * (F10, RN-361) — é entre eles que a tela pergunta o "Pavimento das estacas".
 *
 * Os nomes do pavimento são os da estrutura da obra, e, sem nenhum cadastrado, o rótulo do tipo
 * de pavimento ("Fundação"): a obra que ainda não montou a estrutura continua entendendo a
 * coluna Pavimento escrita assim.
 */
export function destinosDeEstacas(
  obra: Pick<ObraDeEstacas, 'towers' | 'typologies'>,
): readonly DestinoDeEstacas[] {
  return obra.typologies
    .filter(
      (tipologia) =>
        tipologia.kind === 'AREA_COMUM' &&
        (tipologia.floorKind === 'FUNDACAO' || tipologia.floorKind === 'CONTENCAO'),
    )
    .map((tipologia) => {
      const daEstrutura = obra.towers
        .flatMap((torre) => torre.floors)
        .filter((pavimento) => pavimento.kind === tipologia.floorKind)
        .map((pavimento) => pavimento.name);
      return {
        unitTypeId: tipologia.unitTypeId,
        nome: tipologia.name,
        pavimentos: [
          ...new Set(
            daEstrutura.length > 0 ? daEstrutura : [FLOOR_PLAN_KIND_LABEL[tipologia.floorKind]],
          ),
        ],
        existentes: (tipologia.pieces ?? [])
          .filter((peca) => peca.deletedAt === undefined)
          .map((peca) => peca.name),
      };
    });
}

/** Uma linha por tipo × bitola; o tipo sem aço, uma linha com a bitola e os kg vazios. */
function linhasDosTipos(
  tipos: NonNullable<ObraDeEstacas['pileTypes']>,
): readonly (readonly XlsxCell[])[] {
  return tipos
    .filter((tipo) => tipo.deletedAt === undefined)
    .flatMap((tipo): (readonly XlsxCell[])[] =>
      tipo.steel.length === 0
        ? [[tipo.name, tipo.diameter, tipo.length, '', '']]
        : tipo.steel.map((linha) => [
            tipo.name,
            tipo.diameter,
            tipo.length,
            steelGaugeLabel(linha.gauge),
            linha.kgPerPile,
          ]),
    );
}

/** As estacas de cada desenho, na ordem natural do nome ("E2" antes de "E10"). */
function linhasDasEstacas(obra: Partial<ObraDeEstacas>): readonly (readonly XlsxCell[])[] {
  const nomeDoTipo = new Map((obra.pileTypes ?? []).map((tipo) => [tipo.id, tipo.name]));
  return (obra.typologies ?? []).flatMap((tipologia) =>
    (tipologia.pieces ?? [])
      .filter((peca) => peca.deletedAt === undefined)
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { numeric: true }))
      .map((peca) => [peca.name, nomeDoTipo.get(peca.pileTypeId) ?? '', tipologia.name]),
  );
}

/**
 * A planilha de índices, de taxas, de tipos e de estacas da obra, pronta para o `toXlsx`.
 *
 * `disponiveis` é o cadastro de serviços da obra, e entra junto com o catálogo porque o
 * catálogo só tem os serviços que alguém abriu: o modelo que omitisse os outros pediria ao
 * usuário que escrevesse a disciplina e o serviço de cabeça — e o import recusa o nome torto.
 *
 * `obra` traz os tipos e as estacas atuais para as duas abas da fundação; ausente, elas saem
 * só com o cabeçalho.
 *
 * `desativados` são os serviços que a obra desligou: o catálogo do rascunho ainda os guarda, e o
 * modelo que os listasse convidaria a preencher o que o import depois recusa.
 */
export function modeloDeIndices(
  catalogo: ServiceCatalog | undefined,
  disponiveis: readonly ServicoDisponivel[],
  obra: Partial<ObraDeEstacas> = {},
  desativados: readonly ServicoDesativado[] = [],
): readonly XlsxSheet[] {
  const disciplinas = naOrdemDaObra(listarDisciplinas(catalogo, disponiveis, desativados));

  const indices: XlsxSheet = {
    name: 'Índices',
    headers: CABECALHO_INDICES,
    rows: disciplinas.flatMap((disciplina) => {
      const etapa = DO_REGISTRO.get(normalizeName(disciplina.name))?.etapa ?? '';
      return disciplina.services.flatMap((servico) => linhasDoServico(etapa, disciplina, servico));
    }),
  };

  const taxas: XlsxSheet = {
    name: 'Taxas de aço',
    headers: CABECALHO_TAXAS,
    rows: disciplinas
      .filter((disciplina) =>
        disciplina.services.some((servico) => isSteelPurchaseService(servico.name)),
      )
      .flatMap((disciplina) =>
        STEEL_GAUGES.map((bitola) => [
          disciplina.name,
          bitola.label,
          disciplina.steelRates.find((taxa) => taxa.gauge === bitola.code)?.kgPerM3 ?? '',
        ]),
      ),
  };

  const tipos: XlsxSheet = {
    name: 'Tipos',
    headers: CABECALHO_TIPOS,
    rows: linhasDosTipos(obra.pileTypes ?? []),
  };

  const estacas: XlsxSheet = {
    name: 'Estacas',
    headers: CABECALHO_ESTACAS,
    rows: linhasDasEstacas(obra),
  };

  return [indices, taxas, tipos, estacas];
}

/** `"modelo-de-indices-2026-10-05.xlsx"` — o nome diz o que é e de quando. */
export function nomeDoArquivoDoModelo(agora: Date): string {
  return `modelo-de-indices-${agora.toISOString().slice(0, 10)}.xlsx`;
}

/**
 * O cadastro de serviços da obra, na forma que a planilha precisa.
 *
 * O catálogo do rascunho guarda o nome da disciplina, e o serviço do servidor guarda o id dela;
 * a unidade é a mesma história, por `unitOfMeasureId`. Fica de fora o serviço desativado: o
 * cadastro não o oferece mais, e o modelo que o listasse convidaria a preencher o que a obra
 * não mede.
 */
export function servicosDaObra(
  servicos: readonly Pick<
    Service,
    | 'disciplineId'
    | 'name'
    | 'measureBy'
    | 'appliesTo'
    | 'unitOfMeasureId'
    | 'isActive'
    | 'deletedAt'
  >[],
  disciplinas: readonly { readonly id: DisciplineId; readonly name: string }[],
  unidades: readonly Pick<UnitOfMeasure, 'id' | 'name'>[],
): readonly ServicoDisponivel[] {
  const nomeDaDisciplina = new Map(
    disciplinas.map((disciplina) => [disciplina.id, disciplina.name]),
  );
  const nomeDaUnidade = new Map(unidades.map((unidade) => [unidade.id, unidade.name]));

  return servicos
    .filter((servico) => servico.isActive && servico.deletedAt === undefined)
    .flatMap((servico): ServicoDisponivel[] => {
      const disciplina = nomeDaDisciplina.get(servico.disciplineId);
      if (disciplina === undefined) return [];
      const unidade =
        servico.unitOfMeasureId === undefined
          ? undefined
          : nomeDaUnidade.get(servico.unitOfMeasureId);
      return [
        {
          disciplineName: disciplina,
          name: servico.name,
          measureBy: servico.measureBy,
          ...(servico.appliesTo === undefined ? {} : { appliesTo: servico.appliesTo }),
          ...(unidade === undefined ? {} : { unit: unidade }),
        },
      ];
    });
}

/**
 * Os serviços que a obra desativou (ou apagou), na forma que a planilha precisa para recusá-los.
 *
 * É o espelho de `servicosDaObra`: aquele diz quais o cadastro oferece, este quais ele tirou. O
 * catálogo do rascunho nasce do retrato dos itens e guarda os dois, e só o cadastro sabe qual é
 * qual.
 */
export function servicosDesativadosDaObra(
  servicos: readonly Pick<Service, 'disciplineId' | 'name' | 'isActive' | 'deletedAt'>[],
  disciplinas: readonly { readonly id: DisciplineId; readonly name: string }[],
): readonly ServicoDesativado[] {
  const nomeDaDisciplina = new Map(
    disciplinas.map((disciplina) => [disciplina.id, disciplina.name]),
  );
  const chave = (disciplina: string, nome: string): string =>
    `${normalizeName(disciplina)}|${normalizeName(nome)}`;
  const ativos = new Set(
    servicos.flatMap((servico) => {
      const disciplina = nomeDaDisciplina.get(servico.disciplineId);
      return servico.isActive && servico.deletedAt === undefined && disciplina !== undefined
        ? [chave(disciplina, servico.name)]
        : [];
    }),
  );
  return servicos
    .filter((servico) => !servico.isActive || servico.deletedAt !== undefined)
    .flatMap((servico): ServicoDesativado[] => {
      const disciplina = nomeDaDisciplina.get(servico.disciplineId);
      // Um serviço de mesmo nome que a obra ainda usa (o dela e o global) não está desativado.
      return disciplina === undefined || ativos.has(chave(disciplina, servico.name))
        ? []
        : [{ disciplineName: disciplina, name: servico.name }];
    });
}
